import { useRef, useState } from "react";
import {
  ArrowRight,
  FileUp,
  FolderOpen,
  GitCompare,
  Loader2,
  X,
} from "lucide-react";
import {
  fetchNevisignDocumentExport,
  type NevisignDocumentListItem,
} from "@nevisign/reader-core";
import { ensureFreshAccessToken } from "../lib/platform-auth";
import { handleApiAuthError } from "../lib/auth-required";
import { readFileAsTextAttachment } from "../lib/platform-contracts";
import { diffToReaderHtml } from "../lib/textCompare";
import { NevisignDocsPanel } from "./NevisignDocsPanel";
import { ToolPageShell } from "./ToolPages";

type SlotDoc = {
  name: string;
  text: string;
  source: "upload" | "nevisign" | "current";
};

type SlotKey = "primary" | "secondary";

type Props = {
  apiBase: string;
  isAuthenticated: boolean;
  onRequestAuth: (retry: () => void) => void;
  /** Prefill سند اصلی when opened from an already-open document. */
  initialPrimary?: { name: string; text: string } | null;
  /** Open diff HTML in the real DocxViewer / document canvas shell. */
  onOpenResult: (result: {
    title: string;
    html: string;
    commentBodies: string[];
  }) => void;
  onClose: () => void;
  onMinimize?: () => void;
};

type Phase = "setup" | "pick-nevisign";

export function TextComparePage({
  apiBase,
  isAuthenticated,
  onRequestAuth,
  initialPrimary = null,
  onOpenResult,
  onClose,
  onMinimize,
}: Props) {
  const primaryInputRef = useRef<HTMLInputElement>(null);
  const secondaryInputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>("setup");
  const [pickSlot, setPickSlot] = useState<SlotKey>("primary");
  const [primary, setPrimary] = useState<SlotDoc | null>(() =>
    initialPrimary?.text.trim()
      ? {
          name: initialPrimary.name,
          text: initialPrimary.text,
          source: "current",
        }
      : null,
  );
  const [secondary, setSecondary] = useState<SlotDoc | null>(null);
  const [busy, setBusy] = useState(false);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  const setSlot = (slot: SlotKey, doc: SlotDoc | null) => {
    if (slot === "primary") setPrimary(doc);
    else setSecondary(doc);
    setHint(null);
  };

  const loadUploaded = async (slot: SlotKey, file: File) => {
    setBusy(true);
    setHint(null);
    try {
      const extracted = await readFileAsTextAttachment(file);
      const text = extracted.content.trim();
      if (!text) throw new Error("متن قابل استخراج از این فایل یافت نشد.");
      setSlot(slot, {
        name: extracted.name || file.name,
        text,
        source: "upload",
      });
    } catch (err) {
      setHint(err instanceof Error ? err.message : "خواندن فایل ناموفق بود");
    } finally {
      setBusy(false);
    }
  };

  const loadNevisignDoc = async (slot: SlotKey, doc: NevisignDocumentListItem) => {
    setOpeningId(doc.id);
    setBusy(true);
    setHint(null);
    try {
      await ensureFreshAccessToken();
      const exported = await fetchNevisignDocumentExport(apiBase, doc.id);
      const text = (exported.body_cm || exported.body_text || "").trim();
      if (!text) throw new Error("متن سند از سرور خالی برگشت.");
      setSlot(slot, {
        name: exported.title || doc.title || `سند ${doc.id}`,
        text,
        source: "nevisign",
      });
      setPhase("setup");
    } catch (err) {
      if (
        handleApiAuthError(err, () => {
          void loadNevisignDoc(slot, doc);
        })
      ) {
        return;
      }
      setHint(err instanceof Error ? err.message : "دریافت سند ناموفق بود");
    } finally {
      setBusy(false);
      setOpeningId(null);
    }
  };

  const startNevisignPick = (slot: SlotKey) => {
    const run = () => {
      setPickSlot(slot);
      setPhase("pick-nevisign");
      setHint(null);
    };
    if (!isAuthenticated) {
      onRequestAuth(run);
      return;
    }
    run();
  };

  const runCompare = () => {
    if (!primary || !secondary) {
      setHint("هر دو سند را انتخاب کنید.");
      return;
    }
    setBusy(true);
    setHint(null);
    try {
      const { html, commentBodies } = diffToReaderHtml(
        primary.text,
        secondary.text,
        {
          baseAuthor: primary.name,
          changedAuthor: secondary.name,
        },
      );
      const title =
        `مقایسه ${primary.name} و ${secondary.name}`.slice(0, 80).trim() ||
        "نتیجه مقایسه";
      onOpenResult({ title, html, commentBodies });
      onClose();
    } catch (err) {
      setHint(err instanceof Error ? err.message : "مقایسه ناموفق بود");
      setBusy(false);
    }
  };

  const clearSlot = (slot: SlotKey) => {
    setSlot(slot, null);
  };

  if (phase === "pick-nevisign") {
    return (
      <ToolPageShell
        title={
          pickSlot === "primary"
            ? "انتخاب سند اصلی"
            : "انتخاب سند نسخه دوم"
        }
        toolId="compare"
        description="سند موردنظر را از اسناد نویساین انتخاب کنید."
        onClose={onClose}
        onMinimize={onMinimize}
        busy={busy}
        footer={
          <button
            type="button"
            className="ghost-btn"
            onClick={() => setPhase("setup")}
          >
            <ArrowRight size={16} />
            بازگشت به مقایسه
          </button>
        }
      >
        <div className="compare-docs-embed">
          <NevisignDocsPanel
            apiBase={apiBase}
            authenticated={isAuthenticated}
            openingId={openingId}
            onOpenDocument={(doc) => void loadNevisignDoc(pickSlot, doc)}
          />
        </div>
      </ToolPageShell>
    );
  }

  return (
    <ToolPageShell
      title="مقایسه متن"
      toolId="compare"
      onClose={onClose}
      onMinimize={onMinimize}
      busy={busy}
      footer={
        <button
          type="button"
          className="primary-btn"
          disabled={busy || !primary || !secondary}
          onClick={runCompare}
        >
          {busy ? <Loader2 size={16} className="spin" /> : <GitCompare size={16} />}
          مقایسه
        </button>
      }
    >
      <div className="compare-page-slots">
        <CompareSlotCard
          indexLabel="۱"
          label="سند اصلی"
          subtitle="نسخه پایه برای مقایسه"
          variant="primary"
          doc={primary}
          busy={busy}
          inputRef={primaryInputRef}
          onClear={() => clearSlot("primary")}
          onUploadClick={() => primaryInputRef.current?.click()}
          onPickNevisign={() => startNevisignPick("primary")}
          onFile={(file) => void loadUploaded("primary", file)}
        />
        <CompareSlotCard
          indexLabel="۲"
          label="سند نسخه دوم"
          subtitle="نسخه‌ای که با پایه مقایسه می‌شود"
          variant="secondary"
          doc={secondary}
          busy={busy}
          inputRef={secondaryInputRef}
          onClear={() => clearSlot("secondary")}
          onUploadClick={() => secondaryInputRef.current?.click()}
          onPickNevisign={() => startNevisignPick("secondary")}
          onFile={(file) => void loadUploaded("secondary", file)}
        />
      </div>

      {hint && <p className="settings-hint">{hint}</p>}
    </ToolPageShell>
  );
}

function CompareSlotCard({
  indexLabel,
  label,
  subtitle,
  variant,
  doc,
  busy,
  inputRef,
  onClear,
  onUploadClick,
  onPickNevisign,
  onFile,
}: {
  indexLabel: string;
  label: string;
  subtitle: string;
  variant: "primary" | "secondary";
  doc: SlotDoc | null;
  busy: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onClear: () => void;
  onUploadClick: () => void;
  onPickNevisign: () => void;
  onFile: (file: File) => void;
}) {
  const sourceLabel =
    doc?.source === "nevisign"
      ? "از اسناد نویساین"
      : doc?.source === "current"
        ? "سند باز فعلی"
        : "بارگذاری‌شده";

  return (
    <section
      className={`compare-slot-card compare-slot-${variant}${doc ? " is-filled" : ""}`}
      aria-label={`${indexLabel}. ${label}`}
    >
      <div className="compare-slot-head">
        <div className="compare-slot-title-row">
          <span className="compare-slot-badge" aria-hidden>
            {indexLabel}
          </span>
          <div className="compare-slot-title-copy">
            <p className="compare-slot-kicker">
              {variant === "primary" ? "پایه مقایسه" : "نسخه مقابل"}
            </p>
            <h2 className="compare-slot-title">{label}</h2>
            <p className="compare-slot-subtitle">{subtitle}</p>
          </div>
        </div>
        {doc ? (
          <button
            type="button"
            className="icon-btn compact"
            aria-label={`حذف ${label}`}
            disabled={busy}
            onClick={onClear}
          >
            <X size={14} />
          </button>
        ) : null}
      </div>

      {doc ? (
        <div className="compare-slot-selected">
          <strong title={doc.name}>{doc.name}</strong>
          <span>
            {sourceLabel}
            {" · "}
            {doc.text.length.toLocaleString("fa-IR")} نویسه
          </span>
        </div>
      ) : (
        <div className="compare-slot-actions">
          <button
            type="button"
            className="compare-slot-btn compare-slot-btn-cloud"
            disabled={busy}
            onClick={onPickNevisign}
          >
            <FolderOpen size={16} aria-hidden />
            <span>انتخاب از اسناد نویساین</span>
          </button>
          <button
            type="button"
            className="compare-slot-btn compare-slot-btn-upload"
            disabled={busy}
            onClick={onUploadClick}
          >
            <FileUp size={16} aria-hidden />
            <span>بارگذاری سند</span>
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        accept=".doc,.docx,.pdf,.txt,.md,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
      />
    </section>
  );
}
